import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity, Clock, Gauge, TrendingUp, BarChart3 } from 'lucide-react';
import apiService from '../services/api';
import { useMachineData } from '../hooks/useMachineData';
import RealtimeMachineStatus from './RealtimeMachineStatus';

interface MachineLog {
  id: number;
  machineId: number;
  d1?: string; // Legacy field, may exist in old logs
  d2: string;
  d5?: string;
  rpm: number;
  avgRpm: number;
  m: number;
  isOnline: boolean;
  timestamp: string;
  machine: {
    id: number;
    number: string;
    name: string;
  };
}

interface MachineEfficiency {
  machineId: number;
  period: {
    startDate: string;
    endDate: string;
  };
  totalHours: number;
  onlineHours: number;
  efficiency: number;
  averageRPM: number;
    avgRpm: number;
  totalMeters: number;
}

interface MachineLogsProps {
  machineId: number;
  machineNumber: string;
  onClose: () => void;
}

const MachineLogs: React.FC<MachineLogsProps> = ({ machineId, machineNumber, onClose }) => {
  const [timeRange, setTimeRange] = useState('24h'); // 24h, 7d, 30d
  const [activeTab, setActiveTab] = useState<'logs' | 'efficiency'>('efficiency');

  // WebSocket real-time data for this specific machine (use machineId, not machineNumber)
  const { machineData, connectionInfo } = useMachineData(machineId);
  const machineState = Array.isArray(machineData) ? null : machineData;

  // Calculate date range based on timeRange
  const getDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    
    switch (timeRange) {
      case '24h':
        startDate.setHours(startDate.getHours() - 24);
        break;
      case '7d':
        startDate.setDate(startDate.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(startDate.getDate() - 30);
        break;
      default:
        startDate.setHours(startDate.getHours() - 24);
    }
    
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
  };

  const dateRange = getDateRange();

  // Fetch machine logs
  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: ['machine-logs', machineId, timeRange],
    queryFn: () => apiService.get(`/machine-logs/${machineId}/logs?limit=50&startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`),
    enabled: activeTab === 'logs'
  });

  // Fetch machine efficiency from TimescaleDB (high-performance endpoint)
  const { data: efficiencyData, isLoading: efficiencyLoading } = useQuery({
    queryKey: ['machine-efficiency', machineId, timeRange],
    queryFn: () => {
      // Map timeRange to period parameter
      let period = '24h';
      if (timeRange === '7d') period = '24h'; // Use 24h for 7d (can be extended)
      if (timeRange === '30d') period = '24h'; // Use 24h for 30d (can be extended)
      
      return apiService.get(`/machine-logs/${machineId}/efficiency/timescale?period=${period}`);
    },
    enabled: activeTab === 'efficiency'
  });

  const logs: MachineLog[] = logsData?.data || [];
  const efficiency: MachineEfficiency = efficiencyData?.data;

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getStatusColor = (isOnline: boolean) => {
    return isOnline ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100';
  };

  const getStatusText = (isOnline: boolean) => {
    return isOnline ? 'Online' : 'Offline';
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            Machine #{machineNumber} - Logs & Efficiency
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        {/* Real-time WebSocket Status */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <RealtimeMachineStatus
                machineNumber={parseInt(machineNumber)}
                machineState={machineState}
                isConnected={connectionInfo.isConnected}
                error={connectionInfo.status === 'error' ? 'WebSocket connection error' : null}
                showDetails={true}
              />
            </div>
            <div className="text-xs text-gray-500">
              Last Update: {new Date().toLocaleTimeString()}
            </div>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="mb-4">
          <div className="flex space-x-2">
            {['24h', '7d', '30d'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1 text-sm rounded-md ${
                  timeRange === range
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setActiveTab('efficiency')}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'efficiency'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <BarChart3 className="h-4 w-4" />
                <span>Efficiency</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'logs'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <Clock className="h-4 w-4" />
                <span>Logs</span>
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'efficiency' && (
          <div className="space-y-6">
            {efficiencyLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : efficiency ? (
              <>
                {/* Efficiency Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Gauge className="h-5 w-5 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">Efficiency</span>
                    </div>
                    <div className="text-2xl font-bold text-blue-900 mt-1">
                      {efficiency.efficiency.toFixed(1)}%
                    </div>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium text-green-800">Online Hours</span>
                    </div>
                    <div className="text-2xl font-bold text-green-900 mt-1">
                      {efficiency.onlineHours.toFixed(1)}h
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Activity className="h-5 w-5 text-purple-600" />
                      <span className="text-sm font-medium text-purple-800">Avg RPM</span>
                    </div>
                    <div className="text-2xl font-bold text-purple-900 mt-1">
                      {efficiency.avgRpm}
                    </div>
                  </div>
                  
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <TrendingUp className="h-5 w-5 text-orange-600" />
                      <span className="text-sm font-medium text-orange-800">Total Meters</span>
                    </div>
                    <div className="text-2xl font-bold text-orange-900 mt-1">
                      {(efficiency.totalMeters || 0).toFixed(0)}m
                    </div>
                  </div>
                </div>

                {/* Period Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Period</h4>
                  <div className="text-sm text-gray-600">
                    {formatTime(efficiency.period.startDate)} - {formatTime(efficiency.period.endDate)}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Total Hours: {efficiency.totalHours.toFixed(1)}h
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No efficiency data available for the selected period.
              </div>
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="space-y-4">
            {logsLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : logs.length > 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Timestamp
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          RPM
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          D2
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          D5
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {logs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatTime(log.timestamp)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(log.isOnline)}`}>
                              {getStatusText(log.isOnline)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {log.avgRpm}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {log.d2 ?? log.d1 ?? '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {log.d5 ?? '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No logs available for the selected period.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MachineLogs;
